import BrowserUtil from '@/platform/oss/browserUtil'
import type { CloudProjectListItem } from '@/features/project-cloud/list'
import {
  PROJECT_CURRENT_INFO,
  PROJECT_LIST_INDEX,
} from '@/features/project-cloud/ossPaths'

function normalizeScalarText(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/[，,]\s*$/, '')
    .replace(/^['"]+|['"]+$/g, '')
    .trim()
}

function pickText(obj: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = obj[key]
    const text = normalizeScalarText(value)
    if (text) return text
  }
  return ''
}

function pickTimestamp(obj: Record<string, unknown>, keys: string[]): number | string | null {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && normalizeScalarText(value)) return normalizeScalarText(value)
  }
  return null
}

function parseProjectInfo(raw: string): {
  projectName: string
  resolution: string
  updatedAt: number | string | null
} | null {
  const text = raw.trim()
  if (!text) return null

  try {
    const json = JSON.parse(text) as Record<string, unknown>
    return {
      projectName: pickText(json, ['projectName', 'name', 'title']) || '未命名工程',
      resolution: pickText(json, ['resolution', 'screen', 'size']) || '未知分辨率',
      updatedAt: pickTimestamp(json, ['updatedAt', 'lastModified', 'mtime', 'modifyTime']),
    }
  } catch {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && line !== '{' && line !== '}')

    const data: Record<string, string> = {}
    for (const line of lines) {
      const parts = line.split(/[:=]/)
      if (parts.length < 2) continue
      const key = normalizeScalarText(parts[0])
      const value = normalizeScalarText(parts.slice(1).join(':'))
      if (key && value) data[key] = value
    }

    const projectName = pickText(data, ['projectName', 'name', 'title', '工程名称'])
    const resolution = pickText(data, ['resolution', 'screen', 'size', '分辨率'])
    const updatedAt = pickTimestamp(data, [
      'updatedAt',
      'lastModified',
      'mtime',
      'modifyTime',
      'date',
      '最后修改时间',
    ])

    if (!projectName && !resolution && !updatedAt) return null

    return {
      projectName: projectName || '未命名工程',
      resolution: resolution || '未知分辨率',
      updatedAt,
    }
  }
}

function toOrderValue(value: number | string | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const direct = Number(value)
    if (Number.isFinite(direct)) return direct
    const parsed = Date.parse(value)
    if (!Number.isNaN(parsed)) return parsed
  }
  return 0
}

export type OssProjectListItem = CloudProjectListItem & {
  resolution?: string
  projectInfoPath?: string
}

type OssProjectIndexFile = {
  version: 1
  updatedAt: number
  items: OssProjectListItem[]
}

type OssFolderEntry = {
  key: string
  name: string
  relativePath: string
}

function normalizeIndexItem(value: unknown): OssProjectListItem | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const projectKey = normalizeScalarText(row.projectKey ?? row.project_key ?? row.guid ?? row.id)
  if (!projectKey) return null
  return {
    projectKey,
    projectName: normalizeScalarText(row.projectName ?? row.project_name ?? row.name) || '未命名工程',
    updatedAt: pickTimestamp(row, ['updatedAt', 'updated_at', 'lastModified', 'mtime', 'modifyTime']),
    resolution: normalizeScalarText(row.resolution ?? row.screen ?? row.size) || undefined,
    projectInfoPath: normalizeScalarText(row.projectInfoPath ?? row.project_info_path) || undefined,
  }
}

function sortProjectItems(items: OssProjectListItem[]): OssProjectListItem[] {
  return [...items].sort((a: OssProjectListItem, b: OssProjectListItem) => toOrderValue(b.updatedAt) - toOrderValue(a.updatedAt))
}

async function readProjectIndex(oss: BrowserUtil): Promise<OssProjectListItem[] | null> {
  try {
    const raw = await oss.getText(PROJECT_LIST_INDEX)
    const parsed = JSON.parse(raw) as Partial<OssProjectIndexFile>
    if (!Array.isArray(parsed.items)) return null
    return sortProjectItems(
      parsed.items
        .map(normalizeIndexItem)
        .filter((item: OssProjectListItem | null): item is OssProjectListItem => item !== null),
    )
  } catch {
    return null
  }
}

async function writeProjectIndex(oss: BrowserUtil, items: OssProjectListItem[]): Promise<void> {
  const payload: OssProjectIndexFile = {
    version: 1,
    updatedAt: Date.now(),
    items: sortProjectItems(items),
  }
  await oss.writeData(PROJECT_LIST_INDEX, JSON.stringify(payload), {
    contentType: 'application/json;charset=utf-8',
  })
}

async function scanProjectListFromOss(oss: BrowserUtil): Promise<OssProjectListItem[]> {
  const folders = (await oss.listFolders('')) as OssFolderEntry[]

  const items: Array<OssProjectListItem | null> = await Promise.all(
    folders.map(async (folder: OssFolderEntry) => {
      const projectInfoPath = `${folder.relativePath}/${PROJECT_CURRENT_INFO}`
      try {
        const path = (await oss.hasObject(projectInfoPath)) ? projectInfoPath : `${folder.relativePath}/projectInfo.txt`
        if (!(await oss.hasObject(path))) return null
        const text = await oss.getText(path)
        const parsed = parseProjectInfo(text)
        if (!parsed) {
          return null
        }
        return {
          projectKey: folder.name,
          projectName: parsed.projectName,
          updatedAt: parsed.updatedAt,
          resolution: parsed.resolution,
          projectInfoPath,
        } satisfies OssProjectListItem
      } catch {
        return null
      }
    }),
  )

  return sortProjectItems(
    items.filter((item: OssProjectListItem | null): item is OssProjectListItem => item !== null),
  )
}

/**
 * 从 OSS 获取工程索引列表；索引不存在时才回退扫描目录并回写索引。
 * @returns 只包含已找到 `projectInfo.txt` 的工程项。
 */
export async function fetchCloudProjectListFromOss(): Promise<OssProjectListItem[]> {
  const oss = BrowserUtil.getInstance()
  const indexedItems = await readProjectIndex(oss)
  if (indexedItems) return indexedItems

  const scannedItems = await scanProjectListFromOss(oss)
  await writeProjectIndex(oss, scannedItems)
  return scannedItems
}

/**
 * 保存工程后更新 OSS 工程索引，避免启动页反复扫描所有目录。
 * @param item 当前已确认写入 `projectInfo.txt` 的工程列表项。
 */
export async function upsertCloudProjectIndexItem(item: OssProjectListItem): Promise<void> {
  const oss = BrowserUtil.getInstance()
  const currentItems = await readProjectIndex(oss) ?? await scanProjectListFromOss(oss)
  const nextItems = [
    item,
    ...currentItems.filter((existing) => existing.projectKey !== item.projectKey),
  ]
  await writeProjectIndex(oss, nextItems)
}
